// @flow

import React, { PureComponent, Fragment } from "react";
import { noop, get } from "lodash";
import formatSequential from "@tvg/formatter/numeric";
import { caretDown, caretUp } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import type { WagerGroupEnum } from "@fdr/types/WagerTypes";

import {
  Selection,
  FavTag,
  TrendingIcon,
  LabelDescription,
  LabelContainer
} from "./styled-components";
import SequentialLabel from "../SequentialLabel";

export type SelectionType = "default" | "exotic";

type Props = {
  /**
   * Selection's Type
   */
  selectionType: SelectionType,
  /**
   * Selection's number
   */
  number: number,
  /**
   * isChecked state of SelectionButton
   */
  isChecked: boolean,
  /**
   * Scratched state of SelectionButton
   */
  isScratched: boolean,
  /**
   * Callback function after click on SelectionButton
   */
  onChange: () => mixed,
  /**
   * has Hover on button
   */
  hasHover: boolean,
  /**
   * Redirect to racecard, means it is on top races
   */
  redirectToRacecard: boolean,
  /**
   * Is favorite
   */
  isFavorite: boolean,
  /**
   * Current horse odd
   */
  currentOdd: string,

  isAllSelected: boolean,
  trending: ?boolean,
  group: ?WagerGroupEnum,
  qaLabel: string,
  internationalLabel: string,
  wagerTypeCode?: string
};

const onChangeFunc = (
  e: SyntheticEvent<HTMLButtonElement>,
  isScratched: boolean,
  onChange: () => mixed,
  redirectToRacecard: boolean
): void => {
  if (!redirectToRacecard) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (!isScratched) {
    onChange();
  }
};

const groupsLabel = {
  winGroup: ["Win", "Place", "Show"]
};

export default class SelectionButton extends PureComponent<Props> {
  static defaultProps = {
    selectionType: "default",
    number: 0,
    isChecked: false,
    isScratched: false,
    onChange: noop,
    hasHover: false,
    redirectToRacecard: false,
    isFavorite: false,
    currentOdd: "",
    isAllSelected: false,
    trending: null,
    group: null,
    qaLabel: "selectionButton",
    internationalLabel: ""
  };

  formatText() {
    const isWS = this.props.wagerTypeCode === WagerTypeCodesEnum.WIN_SHOW;
    const positions = Array(this.props.number - (isWS ? 1 : 0)).fill();

    const description = this.props.internationalLabel
      ? this.props.internationalLabel
      : positions
          .map((value, index) => {
            // On WS we skip a number so we display "1 or 3" instead of "1 or 2"
            const wsSkip = isWS && index === 1 ? 1 : 0;

            // On index 0 we want to display 1
            const displayNumber = index + 1;

            return formatSequential(wsSkip + displayNumber).toUpperCase();
          })
          .filter(Boolean)
          .join(positions.length > 2 ? "," : " or ");
    return (
      <LabelContainer data-qa-label={`${this.props.qaLabel}Label`}>
        <span data-qa-label={`${this.props.qaLabel}LabelTitle`}>
          {get(
            groupsLabel,
            `${get(this.props, "group", "")}.${this.props.number - 1}`,
            ""
          )}
        </span>
        <LabelDescription
          data-qa-label={`${this.props.qaLabel}LabelDescription`}
        >
          {description}
        </LabelDescription>
      </LabelContainer>
    );
  }

  renderTrending = (isButtonActive: boolean) =>
    this.props.trending ? (
      <TrendingIcon
        data-qa-label={`${this.props.qaLabel}TrendingIcon`}
        icon={caretUp}
        color={
          isButtonActive
            ? buildColor("white", "100")
            : buildColor("green", "500")
        }
      />
    ) : (
      <TrendingIcon
        data-qa-label={`${this.props.qaLabel}TrendingIcon`}
        icon={caretDown}
        color={
          isButtonActive ? buildColor("white", "100") : buildColor("red", "500")
        }
      />
    );

  renderNormal() {
    return (
      <Fragment>
        {this.props.isFavorite && (
          <Fragment>
            <FavTag
              size={16}
              name="FAV"
              tagType="FavTag"
              data-qa-label={`${this.props.qaLabel}FavoriteTag`}
            />
          </Fragment>
        )}

        {this.props.trending !== null &&
          this.renderTrending(this.props.isChecked)}
        {this.props.currentOdd}
      </Fragment>
    );
  }

  renderLabel() {
    return this.props.group ? (
      this.formatText()
    ) : (
      <SequentialLabel
        type="span"
        label={formatSequential(this.props.number)}
        qaLabel={`${this.props.qaLabel}SequentialLabel`}
      />
    );
  }

  render() {
    return (
      <Selection
        onClick={(e: SyntheticEvent<HTMLButtonElement>): void => {
          e.stopPropagation();
          return onChangeFunc(
            e,
            this.props.isScratched,
            this.props.onChange,
            this.props.redirectToRacecard
          );
        }}
        isAllSelected={this.props.isAllSelected}
        qaLabel={this.props.qaLabel}
        selectionType={this.props.selectionType}
        isChecked={this.props.isChecked}
        isFavorite={this.props.isFavorite}
      >
        {this.props.selectionType === "default"
          ? this.renderNormal()
          : this.renderLabel()}
      </Selection>
    );
  }
}
